<template>
	<transition-group name="fade">
		<slot v-if="$cookies.get('userToken')">
		<div class="w-full bg-leftPanelBG p-1.5 sm:p-2 shadow-light fixed top-0 z-30">
			<div class="flex gap-4 float-right sm:mr-3 items-center text-leftPanelText">
				<div class="rounded-full text-sm" v-if="free_trial">
					{{ $t('free_trial') }} {{ Math.ceil((new Date(free_trial) - new Date()) / (1000 * 60 * 60 * 24)) + 1 }} {{ $t('days') }}
				</div>
				<div @click="toggleDarkMode" class="flex text-leftPanelText items-center leading-4 cursor-pointer text-center">
					<div class="text-xs sm:text-sm mr-1">{{ $t('dark_mode') }}</div>
					<div><font-awesome-icon :icon="icon" class="text-lg text-white"/></div>
				</div>
				<div class="w-max text-black mx-auto">
					<language-picker />
				</div>
				<router-link class="text-sm text-white" to="/settings">
					<font-awesome-icon :icon="['fas', 'fa-cogs']"/>
				</router-link>
				<a href="https://creatorxone.gitbook.io/creatorxone" target="_blank" class="text-sm text-white">
					<font-awesome-icon :icon="['far', 'fa-circle-question']"/>
				</a>
				<div class="flex items-center text-sm cursor-pointer" @click="$router.push('/profile')">
					<div alt="Profile Picture" class="w-6 h-6 rounded-full mr-2 bg-white text-button text-xs p-1 px-0.5 text-center">{{ initials }}</div>
					<div class="w-20 sm:w-24 truncate" :class="{'text-center': !userName}">
						<span v-if="userName">{{ userName }}</span>
						<span v-else><font-awesome-icon :icon="['fas', 'fa-spinner']" class="animate-spin" /></span>
					</div>
				</div>
			</div>
			<div class="clear-both"></div>
		</div>
		<div class="w-full h-10 bg-bkg"></div>

		<div class="rounded-t-3xl hidden sm:block sm:rounded-none sm:rounded-r-xl bg-leftPanelBG w-full h-20 sm:h-full fixed bottom-0 sm:top-0 pt-1 text-white sm:justify-items-left z-30 sm:w-20" style="box-shadow: rgba(100, 100, 100, 0.25) 5px 0px 5px;">
			
            <div class="flex items-center ml-4">
                <router-link to="/analytics">
                    <div class="flex gap-2 items-center">
                        <div class="ml-4 text-2xl font-semibold" style="font-size:19px;font-family: 'Exo', sans-serif;">Creator<span class="font-bold px-1" style="color:#10B981;font-size:24px;">X</span>one</div>
                        <div v-if="isElectron && appVersion" class="text-xs text-center text-leftPanelText mt-2">						
                            v{{ appVersion }}
                        </div>
                    </div>
                </router-link>
                <font-awesome-icon v-if="isElectron" icon="fas fa-sync-alt" class="cursor-pointer ml-4" @click="refreshPage" />
            </div>
			
			<div v-if="menuItems" class="mt-10">
				<div v-for="(item, index) in menuItems" :key="index"
					@mouseenter="toggleChildren(item.name, true)" 
            		@mouseleave="toggleChildren(item.name, false), closeAllChildren()"
                    @click="menuItemClick(item.children)">

					<div class="rounded-lg hover:bg-leftPanelText hover:bg-opacity-10 text-center mx-1 block cursor-pointer text-lg leading-14 py-2 my-1.5 text-leftPanelText hover:text-white font-semibold">
						<div class="whitespace-nowrap" style="margin-left:1px">
							<router-link v-if="item.link" :to="'../../' + item.link">
								<div class="px-6">
									<font-awesome-icon :icon="item.icon" class="w-5 mr-0"/>
									<div class="text-xs font-semibold -ml-7 text-center w-20 truncate" :title="item.name">
										{{ item.name }}
									</div>
								</div>
							</router-link>
							<div v-else @click="callMethod(item.method)">
								<div class="px-6">
									<font-awesome-icon :icon="item.icon" class="w-5 mr-0"/>
									<div class="text-xs font-semibold -ml-7 text-center w-20 truncate" :title="item.name">
										{{ item.name }}
									</div>
								</div>
							</div>
						</div>
						<section v-show="item.children && item.showChildren" 
							class="absolute text-left -mt-14 border-0 border-l border-bkg dark:border-dark.bkg left-20 bg-leftPanelBG text-tooltipText sm:leading-8 whitespace-nowrap z-30 rounded-lg p-2 px-4 text-base shadow-light dark:shadow-dark">
							<slot v-for="(child, chI) in item.children" :key="chI">
								<router-link v-if="child?.link" :to="'../../' + child.link">
									<div class="block sm:w-full font-normal cursor-pointer hover:text-gray-200">{{ child.name }}</div>
								</router-link>
							</slot>
						</section>
					</div>
				</div>
				<a href="https://creatorxone.gitbook.io/creatorxone" target="_blank">
					<div class="px-6 mx-1 text-lg leading-14 text-leftPanelText hover:text-white font-semibold rounded-lg hover:bg-leftPanelText hover:bg-opacity-10 text-center p-2 px-4">
						<font-awesome-icon :icon="['far', 'fa-circle-question']" class="w-5 mr-0"/>
						<div class="text-xs font-semibold -ml-7 text-center w-20 truncate" :title="$t('help_center')">
							{{ $t('help_center') }}
						</div>
					</div>
				</a>
			</div>
			
			<div v-if="menuItems" @click="handleLogout()" class="sm:w-full cursor-pointer p-4 px-8 text-lg leading-14 bottom-0 absolute text-leftPanelText hover:text-white font-semibold whitespace-nowrap">
				<font-awesome-icon icon="fas fa-arrow-right-from-bracket" class="w-5 mr-2.5"/>
				<div class="text-xs -ml-8 w-20 text-center">{{ $t('logout') }}</div>
			</div>
		</div>
		
		<div class="z-40 sm:hidden fixed bottom-0 left-0 bg-leftPanelBG text-white rounded-t-2xl w-full p-3 px-5">
			<font-awesome-icon @click="displayMenu = !displayMenu" icon="fas fa-bars" />
		</div>
		
		<section v-if="displayMenu" class="overflow-auto z-50 w-full h-full fixed p-5 top-0 left-0 bg-leftPanelBG text-white">
			<font-awesome-icon @click="displayMenu = false" icon="fas fa-xmark" class="absolute right-0 fa-lg top-1 p-5" />
			<div class="container p-3 pt-2 mx-auto leading-loose">
				<section class="whitespace-nowrap mb-5" v-if="userName">
					<div class="w-full truncate">{{$t('welcome_back')}} {{userName}}</div>
					<div><strong>{{ $t('agency') }}:</strong> {{branchName}}</div>
				</section>
				
				<slot v-if="menuItems">
					<slot v-for="(item,index) in menuItems" :key="index">
						<!-- Pridať child options -->
						<router-link @click="displayMenu = false" v-if="item.link" :to="'../../' + item.link">
							<div><font-awesome-icon :icon="item.icon" class="mr-2" /> {{item.name}}</div>
						</router-link>
						<span v-else @click="callMethod(item.method)" class="cursor-pointer font-semibold">
							<font-awesome-icon :icon="item.icon" class="mr-2" /> {{item.name}}
						</span>
						<div v-if="item.children">
							<router-link @click="displayMenu = false" :to="'../../' + child.link" v-for="(child,chI) in item.children" :key="chI">
								<div class="pl-10">
									● {{child.name}}</div>
							</router-link>
						</div>
					</slot>
					<br />
					<a href="https://creatorxone.gitbook.io/creatorxone" target="_blank">
						<font-awesome-icon icon="far fa-circle-question" class="mr-2" /> {{ $t('help_center') }}
					</a>
					<br />
					<div @click="handleLogout()" class="">
						<font-awesome-icon icon="fas fa-arrow-right-from-bracket" class="mr-2" /> Logout
					</div>
					<label class="text-xs text-white">{{ $t('language')}}:</label><br />
					<language-picker />
				</slot>
			</div>
		</section>
		
	</slot>
</transition-group>
</template>

<script setup>
    import logoComponent from '@/components/logo.vue';
    import languagePicker from '@/components/languagePicker.vue';
</script>

<script>
import { useAuthStore } from '@/stores/auth';
import { api_get_call, logout, get_menu } from '@/api/api.js';

export default {
    components: { logoComponent, languagePicker },
    data() {
        return {
            isElectron: this.$cookies.get('desktop_app_token') == import.meta.env.VITE_API_INTERNAL_KEY_LIST ?? false,
            appVersion: null,
            bigMenu: false,
            menuOptions: [],
            menuOpened: false,
            compSelector: { show: false, over: false },
            userName: null,
            free_trial: null,
            usernameOver: false,
            branchNameOver: false,
            branchName: null,
            backgroundOn: '0',
            displayMenu: false,
			menuItems: [],
            mode: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
            icon: document.documentElement.classList.contains('dark') ? 'fas fa-toggle-on' : 'fas fa-toggle-off',
            menuLoaded: false, // Add this line
        };
    },
    computed: {
        initials() { 
            if (!this.userName) return '';
            const names = this.userName.split(' ');
            const initials = names.length > 1 ? names[0][0] + names[names.length - 1][0] : names[0][0];
            return initials.toUpperCase().slice(0, 2);
        },
        authStore() {
            return useAuthStore();
        },
        myself() {
            return this.authStore.myself;
        },
        permissions() {
            return this.authStore.permissions;
        }
    },
    emits: ['changeComp'],
    methods: {
        refreshPage() {
            window.location.reload();
        },
        toggleDarkMode() {
            this.mode = this.mode === 'light' ? 'dark' : 'light';
            this.icon = this.icon === 'fas fa-toggle-off' ? 'fas fa-toggle-on' : 'fas fa-toggle-off';
            const html = document.documentElement;
            html.classList.toggle('dark');
            localStorage.setItem("darkMode", this.mode.toString() === 'dark');
        },
        toggleChildren(itemName, show) {
            if (!this.menuLoaded) return; // Add this line
            const item = this.menuItems.find(item => item.name === itemName);
            if (item) {
                item.showChildren = show;
            }
        },
        closeAllChildren() {
            this.menuItems.forEach(item => {
                if (item.children) {
                    item.showChildren = false;
                }
            });
        },
        menuItemClick(children) {
            if (children && children.length == 1) {
                this.$router.push({ path: "../../" + children[0].link });
            }
        },
        callMethod(methodName) {
            if (methodName && typeof this[methodName] === 'function') {
                this[methodName]();
            }
        },
        changeBG() {
            this.backgroundOn = (parseInt(this.backgroundOn) + 1) % 2;
            if (this.backgroundOn != '1') {
                document.body.classList.add('no-bg');
            } else {
                document.body.classList.remove('no-bg');
            }
            this.$cookies.set('bgOn', this.backgroundOn);
        },
        openMenu(index) {
            this.menuOptions[index].over = true;
        },
        closeMenu() {
            this.menuOpened = false;
        },
        changeComp() {
            this.$emit('changeComp');
        },
        createMenu() {
            let items = [
                {
                    name: this.$t('Creators'),
                    icon: "fas fa-users",
                    showChildren: false,
                    children: [
                        { name: this.$t('Profiles'), link: "creators" },
                        this.$getPermission(this.permissions, 16).is_granted ? { name: this.$t('Transactions'), link: "transactions" } : null,
                        this.$getPermission(this.permissions, 16).is_granted ? { name: this.$t('add_ons'), link: "addons" } : null,
                    ].filter(child => child !== null),
                },
                {
                    name: this.$t('Dashboard'),
                    icon: "fas fa-chart-column",
                    showChildren: false,
                    children: [
                        this.$getPermission(this.permissions, 1).is_granted ? { name: this.$t('Metrics'), link: "metrics" } : null,
                        this.$getPermission(this.permissions, 1).is_granted ? { name: this.$t('Analytics'), link: "analytics" } : null,
                    ].filter(child => child !== null),
                },
                {
                    name: this.$t('Employees'),
                    icon: "fa-solid fa-building-user",
                    showChildren: false,
                    children: [
                        this.$getPermission(this.permissions, 9).is_granted || this.$getPermission(this.permissions, 10).is_granted ? { name: this.$t('Profiles'), link: "employees" } : null,
                        this.$getPermission(this.permissions, 4).is_granted || this.$getPermission(this.permissions, 5).is_granted ? { name: this.$t('Shifts'), link: "shifts" } : null,
                        this.$getPermission(this.permissions, 4).is_granted || this.$getPermission(this.permissions, 5).is_granted ? { name: this.$t('Shift_earnings'), link: "sexting" } : null,
                        this.$getPermission(this.permissions, 9).is_granted || this.$getPermission(this.permissions, 10).is_granted ? { name: this.$t('Chat Scripts'), link: "chattingScripts" } : null,
                    ].filter(child => child !== null),
                },
                {
                    name: this.$t('Finance'),
                    icon: "fas fa-coins",
                    showChildren: false,
                    children: [
                        this.$getPermission(this.permissions, 21).is_granted ? { name: this.$t('Payroll'), link: "payrolls" } : null,
                        this.$getPermission(this.permissions, 21).is_granted ? { name: this.$t('Sexting_cost'), link: "sextingCosts" } : null,
                        [1, 2].includes(this.myself?.position) ? { name: this.$t('Billing'), link: "Billing" } : null,
                    ].filter(child => child !== null),
                },
                {
                    name: this.$t('Traffic'),
                    icon: "fas fa-bullhorn",
                    showChildren: false,
                    children: [
                        this.$getPermission(this.permissions, 17).is_granted ? { name: this.$t('Tracking_Links'), link: "trackingLinks" } : null,
                        this.$getPermission(this.permissions, 17).is_granted ? { name: this.$t('Trial_Links'), link: "trialLinks" } : null,
                    ].filter(child => child !== null),
                },
                {
                    name: this.$t('Download'),
                    icon: "fa-solid fa-download",
                    link: "tools",
                    showChildren: false,
                },
                {
                    name: this.$t('Settings'),
                    icon: "fa-solid fa-cogs",
                    link: "settings",
                    showChildren: false,
                },
                (this.myself?.multi_branch_access !== null && !this.myself?.multi_branch_access) ? null
                    : {
                        name: this.$t('Switch_branch'),
                        icon: "fa-solid fa-shuffle",
                        method: "changeComp",
                    },
            ].filter(item => item !== null && (item.children?.length > 0 || item.link || item.method));

            return items;
        },
        async getAppVersion(retryCount = 3, delay = 300) {
            if (!this.isElectron) return;
            if (window?.electron?.getAppVersion) {
                try {
                    const version = await window.electron.getAppVersion();
                    console.log('Electron version:', version);
                    this.appVersion = version;
                } catch (error) {
                    console.error('Failed to fetch Electron version:', error);
                }
            } else if (retryCount > 0) {
                console.log(`Electron API not ready, retrying... (${retryCount} retries left)`);
                setTimeout(() => this.getAppVersion(retryCount - 1, delay), delay);
            } else {
                console.error('Failed to fetch Electron version: Electron API not available.');
            }
        },
        async handleLogout() {
            await logout();
            this.authStore.clearAuthData();
            window.location.href = '/';
        },
    },
    async created() {
        await this.getAppVersion();
        if (this.$cookies.get('bgOn') == null) {
            this.$cookies.set('bgOn', '1');
            this.backgroundOn = '1';
        } else {
            this.backgroundOn = this.$cookies.get('bgOn');
        }
        if (this.backgroundOn != '1') {
            document.body.classList.add('no-bg');
        } else {
            document.body.classList.remove('no-bg');
        }
    },
    async mounted() {
        const companyID = this.$cookies.get('c_id') || null;
        const menu = await get_menu(this.$cookies.get('userToken'), companyID);
        if (menu && menu.logout === true) {
            this.$emit('changeComp');
        } else {
            this.userName = menu.username;
            this.free_trial = menu?.free_trial ?? null;
            this.branchName = menu.branchName;
            if (this.$cookies.get('c_id')) {
                this.compSelector.show = true;
            }
        }

        if (this.$route.path !== "/tools") {
            let agency_status = await api_get_call(`users/?action=agency_profile_status&companyID=${this.$cookies.get('c_id')}&userToken=${this.$cookies.get('userToken')}`);
            this.agency_status = agency_status.to_do;
            if (this.agency_status && (this.agency_status[0].done == false || this.agency_status[1].done == false) || agency_status.subscription_active == false) {
                this.$router.push({ name: 'Profile' });
            }
        }

		this.$watch('permissions', (newVal) => {
			if (newVal) {
			this.menuItems = this.createMenu();
            this.menuLoaded = true; // Add this line
			}
		}, { immediate: true });
    },
};
</script>